import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  AccountBalanceWallet,
  Receipt,
  Assessment,
  CalendarToday,
  TrendingUp,
  AccountBalance,
  Business,
  Notifications,
  Security,
  Logout,
  Person,
  Settings,
  School,
  EmojiEvents,
  MenuBook,
  Group,
  LibraryBooks,
  Balance,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

// Types
interface LayoutProps {
  children: React.ReactNode;
}

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  badge?: number;
}

// Configuration de la navigation
const navigationItems: MenuItem[] = [
  {
    text: 'Tableau de bord',
    icon: <Dashboard />,
    path: '/dashboard',
  },
  {
    text: 'Plan comptable',
    icon: <AccountBalanceWallet />,
    path: '/plan-comptable',
  },
  {
    text: 'Écritures',
    icon: <Receipt />,
    path: '/ecritures',
  },
  {
    text: 'Journaux',
    icon: <MenuBook />,
    path: '/journaux',
  },
  {
    text: 'Grand livre',
    icon: <LibraryBooks />,
    path: '/grand-livre',
  },
  {
    text: 'Balance',
    icon: <Balance />,
    path: '/balance',
  },
  {
    text: 'États financiers',
    icon: <Assessment />,
    path: '/etats-financiers',
  },
  {
    text: 'Exercices',
    icon: <CalendarToday />,
    path: '/exercices',
  },
  {
    text: 'Analytics',
    icon: <TrendingUp />,
    path: '/analytics',
  },
  {
    text: 'Rapprochement',
    icon: <AccountBalance />,
    path: '/rapprochement',
  },
  {
    text: 'Entités EBNL',
    icon: <Business />,
    path: '/entites',
  },
  {
    text: 'Tiers',
    icon: <Group />,
    path: '/tiers',
  },
  {
    text: 'Notifications',
    icon: <Notifications />,
    path: '/notifications',
    badge: 3, // Exemple de badge
  },
  {
    text: 'Audit',
    icon: <Security />,
    path: '/audit',
  },
  {
    text: 'Formation EBNL',
    icon: <School />,
    path: '/learning',
  },
  {
    text: 'Certificats',
    icon: <EmojiEvents />,
    path: '/learning/certificats',
  },
];

const drawerWidth = 280;

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Gestion du menu mobile
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Gestion du menu utilisateur
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Navigation
  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  // Contenu du drawer
  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header du sidebar */}
      <Box sx={{ p: 3, textAlign: 'center', backgroundColor: 'primary.main', color: 'white' }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
          ComptaEBNL-IA
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.8 }}>
          Comptabilité EBNL avec IA
        </Typography>
      </Box>

      <Divider />

      {/* Menu de navigation */}
      <List sx={{ flexGrow: 1, px: 1, py: 2 }}>
        {navigationItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() => handleNavigation(item.path)}
              selected={location.pathname === item.path}
              sx={{
                borderRadius: 2,
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                  },
                },
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: location.pathname === item.path ? 'white' : 'inherit',
                  minWidth: 40,
                }}
              >
                {item.badge ? (
                  <Badge badgeContent={item.badge} color="error">
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: location.pathname === item.path ? 600 : 400,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider />

      {/* Informations utilisateur */}
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Connecté en tant que
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {user?.prenom} {user?.nom}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {user?.role}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: 'white',
          color: 'text.primary',
          boxShadow: 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {navigationItems.find(item => item.path === location.pathname)?.text || 'ComptaEBNL-IA'}
          </Typography>

          {/* Actions de la topbar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Badge notifications */}
            <IconButton color="inherit">
              <Badge badgeContent={3} color="error">
                <Notifications />
              </Badge>
            </IconButton>

            {/* Menu utilisateur */}
            <IconButton onClick={handleUserMenuOpen}>
              <Avatar
                sx={{
                  bgcolor: 'primary.main',
                  width: 32,
                  height: 32,
                  fontSize: '0.875rem',
                }}
              >
                {user?.prenom?.[0]}{user?.nom?.[0]}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Menu utilisateur */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleUserMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleUserMenuClose}>
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          Mon profil
        </MenuItem>
        <MenuItem onClick={handleUserMenuClose}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          Paramètres
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Déconnexion
        </MenuItem>
      </Menu>

      {/* Navigation drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* Contenu principal */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: 'background.default',
        }}
      >
        <Toolbar /> {/* Espaceur pour l'AppBar */}
        {children}
      </Box>
    </Box>
  );
};

export default Layout;